import { requestPermission, setupForegroundListener } from '../firebase/config';

export interface NotificationPayload {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  data?: any;
}

class NotificationService {
  private token: string | null = null;
  private isSupported: boolean = false;
  private isListening: boolean = false;
  private isInitializing: boolean = false;
  private hasInitialized: boolean = false;
  private readonly FCM_TOKEN_STORAGE_KEY = 'fcm_token';

  constructor() {
    this.isSupported = 'Notification' in window && 'serviceWorker' in navigator;
    // Load token from localStorage on initialization
    this.loadTokenFromStorage();
  }

  // Load FCM token from localStorage
  private loadTokenFromStorage(): void {
    try {
      const storedToken = localStorage.getItem(this.FCM_TOKEN_STORAGE_KEY);
      if (storedToken) {
        this.token = storedToken;
        console.log('FCM token loaded from localStorage');
      }
    } catch (error) {
      console.error('Failed to load FCM token from localStorage:', error);
    }
  }

  // Save FCM token to localStorage
  private saveTokenToStorage(token: string): void {
    try {
      localStorage.setItem(this.FCM_TOKEN_STORAGE_KEY, token);
      console.log('FCM token saved to localStorage');
    } catch (error) {
      console.error('Failed to save FCM token to localStorage:', error);
    }
  }

  // Get stored FCM token from localStorage
  private getStoredToken(): string | null {
    try {
      return localStorage.getItem(this.FCM_TOKEN_STORAGE_KEY);
    } catch (error) {
      console.error('Failed to get FCM token from localStorage:', error);
      return null;
    }
  }

  // Initialize notification service
  async initialize(): Promise<boolean> {
    // Prevent duplicate initialization calls
    if (this.hasInitialized) {
      console.log('Notifications already initialized, skipping duplicate call');
      return this.token !== null;
    }

    // Prevent concurrent initialization calls
    if (this.isInitializing) {
      console.log('Notification initialization already in progress, skipping duplicate call');
      return false;
    }

    if (!this.isSupported) {
      console.warn('This browser does not support notifications');
      return false;
    }

    this.isInitializing = true;

    try {
      const newToken = await requestPermission();
      if (newToken) {
        const storedToken = this.getStoredToken();
        
        // Only update if token has changed
        if (newToken !== storedToken) {
          this.token = newToken;
          // Save token to localStorage
          this.saveTokenToStorage(newToken);
          
          // Send token to server for registration only if it changed
          await this.sendTokenToServer(newToken);
          console.log('FCM token registered with server (token changed)');
        } else {
          // Token hasn't changed, just use the stored one
          this.token = newToken;
          console.log('FCM token unchanged, skipping server update');
        }
      }
      this.hasInitialized = true;
      return this.token !== null;
    } catch (error) {
      console.error('Failed to initialize notifications:', error);
      return false;
    } finally {
      this.isInitializing = false;
    }
  }

  // Get FCM token
  getToken(): string | null {
    return this.token;
  }

  /** Remove persisted FCM token and reset init state (call on logout / session expiry). */
  clearFcmToken(): void {
    try {
      localStorage.removeItem(this.FCM_TOKEN_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    this.token = null;
    this.hasInitialized = false;
    this.isInitializing = false;
  }

  // Check if notifications are supported
  isNotificationSupported(): boolean {
    return this.isSupported;
  }

  // Test notification function
  testNotification(): void {
    console.log('Testing notification...');
    
    // Try a simple notification first
    try {
      const simpleNotification = new Notification('Simple Test', {
        body: 'This is a simple test notification'
      });
      console.log('Simple notification created:', simpleNotification);
    } catch (error) {
      console.error('Error creating simple notification:', error);
    }
    
    // Then try our full notification
    this.showNotification({
      title: 'Test Notification',
      body: 'This is a test notification from Menunix',
      data: { page: 'orders' }
    });
  }

  // Check if permission is granted
  isPermissionGranted(): boolean {
    return Notification.permission === 'granted';
  }

  // Show local notification
  showNotification(payload: NotificationPayload): void {
    console.log('Attempting to show notification:', payload);
    console.log('Current notification permission:', Notification.permission);
    
    if (!this.isPermissionGranted()) {
      console.warn('Notification permission not granted');
      return;
    }

    try {
      const notification = new Notification(payload.title, {
        body: payload.body,
        icon: payload.icon || '/favicon.ico',
        badge: payload.badge || '/favicon.ico',
        data: payload.data,
        requireInteraction: true, // Make it more prominent like service worker
        silent: false // Ensure it makes sound
      });

      console.log('Notification created successfully:', notification);

      // Check if notification is actually visible
      setTimeout(() => {
        console.log('Notification state after 1 second:', {
          title: notification.title,
          body: notification.body
        });
      }, 1000);

      // Don't auto-close for debugging
      // setTimeout(() => {
      //   notification.close();
      // }, 8000);

      // Handle click - navigate to orders page like service worker
      notification.onclick = () => {
        console.log('Foreground notification clicked');
        window.focus();
        notification.close();
        
        // Navigate to orders page
        if (payload.data && payload.data.page) {
          console.log('Navigating to page from foreground notification:', payload.data.page);
          // Dispatch custom event for page navigation
          const navigationEvent = new CustomEvent('notificationNavigation', {
            detail: { page: payload.data.page, data: payload.data }
          });
          window.dispatchEvent(navigationEvent);
        } else {
          // Default to orders page if no specific page in data
          console.log('Navigating to default orders page');
          const navigationEvent = new CustomEvent('notificationNavigation', {
            detail: { page: 'orders', data: payload.data }
          });
          window.dispatchEvent(navigationEvent);
        }
      };

      // Handle error
      notification.onerror = (error) => {
        console.error('Notification error:', error);
      };

      // Handle show event
      notification.onshow = () => {
        console.log('Notification shown successfully');
      };

      // Handle close event
      notification.onclose = () => {
        console.log('Notification closed');
      };

    } catch (error) {
      console.error('Error creating notification:', error);
    }
  }

  // Listen for foreground messages
  startListening(): void {
    if (this.isListening) {
      console.log('Foreground listener already set up, skipping...');
      return;
    }

    console.log('Setting up foreground message listener...');
    this.isListening = true;
    
    // Set up continuous listening for foreground messages
    setupForegroundListener((payload: any) => {
      console.log('Received foreground message:', payload);
      
      // Check if we have notification data
      if (payload.notification) {
        console.log('Notification data found:', payload.notification);
      } else {
        console.log('No notification data in payload, using fallback');
      }
      
      // If this is a waiter call notification, emit an app-level event for UI modals
      try {
        const data = payload?.data || {};
        if (
          data.page === 'notification' &&
          (data.to === 'waiter' || data.to === 'staff') &&
          (data.reason === 'bill' || data.reason === 'other') &&
          data.table
        ) {
          const waiterEvent = new CustomEvent('waiterCall', {
            detail: {
              table: String(data.table),
              reason: String(data.reason),
              raw: data
            }
          });
          window.dispatchEvent(waiterEvent);
        }
      } catch (e) {
        console.warn('Failed to dispatch waiterCall event', e);
      }

      // Show notification when app is in foreground
      const notificationPayload = {
        title: payload.data?.title || 'New Notification',
        body: payload.data?.body || 'You have a new message',
        icon: payload.data?.logo,
        data: payload.data
      };

      // Notify the app immediately when foreground push arrives.
      // This lets orders refresh without requiring notification click.
      window.dispatchEvent(
        new CustomEvent('foregroundNotificationReceived', {
          detail: { data: payload.data || {} }
        })
      );

      console.log('Notification payload:', payload);
      
      console.log('Calling showNotification with:', notificationPayload);
      this.showNotification(notificationPayload);
    });
    console.log('Foreground message listener set up successfully');
  }

  // Send token to server
  async sendTokenToServer(token: string): Promise<boolean> {
    try {
      const authToken = localStorage.getItem('auth_token');
      const response = await fetch(import.meta.env.VITE_API_BASE_URL+'/shopOwnerTable/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken && { Authorization: `Bearer ${authToken}` })
        },
        body: JSON.stringify({
          fcmToken: token
        })
      });

      return response.ok;
    } catch (error) {
      console.error('Failed to send token to server:', error);
      return false;
    }
  }

}

// Create singleton instance
export const notificationService = new NotificationService();

// Make it available globally for testing
if (typeof window !== 'undefined') {
  (window as any).notificationService = notificationService;
}

export default notificationService;
